import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { map, distinctUntilChanged, startWith } from "rxjs/operators";

import { UlaesteDokumenterService } from "../edh/ulaeste-dokumenter.service";

export interface TaskbarBadgeData {
    unreadDocuments: number;
    totalUnread: number;
    source?: "service"; // Kilde til data
    timestamp?: number; // Tidspunkt for sidste opdatering
}

@Injectable({ providedIn: "root" })
export class TaskbarBadgeService {
    private readonly ulaesteDokumenterService = inject(UlaesteDokumenterService);

    private readonly badgeData$ = this.ulaesteDokumenterService.hentAntalUlaesteDokumenter().pipe(
        startWith(0),
        map((unreadDocuments) => {
            const result = {
                unreadDocuments,
                totalUnread: unreadDocuments,
                source: "service" as const,
                timestamp: Date.now()
            };
            return result;
        }),
        distinctUntilChanged((prev, curr) => prev.totalUnread === curr.totalUnread)
    );

    constructor() {
        this.initializeTaskbarBadge();
    }

    /**
     * Henter observable med badge data
     */
    public getBadgeData(): Observable<TaskbarBadgeData> {
        return this.badgeData$;
    }

    /**
     * Initialiserer taskbar badge funktionaliteten
     */
    private initializeTaskbarBadge(): void {
        this.badgeData$.subscribe((badgeData) => {
            this.updateTaskbarBadge(badgeData);
        });
    }

    /**
     * Opdaterer taskbar badge med antal ulæste elementer
     */
    private updateTaskbarBadge(badgeData: TaskbarBadgeData): void {
        if (this.isPWAInstalled()) {
            this.updatePWABadge(badgeData);
        } else {
            this.updateFaviconBadge(badgeData);
        }
    }

    /**
     * Tjekker om appen kører som PWA
     */
    private isPWAInstalled(): boolean {
        return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone || document.referrer.includes("android-app://");
    }

    /**
     * Opdaterer PWA badge (Windows taskbar)
     */
    private updatePWABadge(badgeData: TaskbarBadgeData): void {
        if ("setAppBadge" in navigator) {
            // Modern browsers with Badging API
            try {
                if (badgeData.totalUnread > 0) {
                    (navigator as any).setAppBadge(badgeData.totalUnread);
                } else {
                    (navigator as any).clearAppBadge();
                }
            } catch (error) {
                console.error("Kunne ikke opdatere PWA badge:", error);
            }
        } else if ("ExperimentalBadge" in window) {
            // Chrome experimental badge API
            try {
                const badge = (window as any).ExperimentalBadge;
                if (badgeData.totalUnread > 0) {
                    badge.set(badgeData.totalUnread);
                } else {
                    badge.clear();
                }
            } catch (error) {
                console.error("Kunne ikke opdatere Chrome experimental badge:", error);
            }
        } else {
            // Fallback til favicon badge
            this.updateFaviconBadge(badgeData);
        }
    }

    /**
     * Opdaterer favicon med badge (fallback metode)
     */
    private updateFaviconBadge(badgeData: TaskbarBadgeData): void {
        if (badgeData.totalUnread === 0) {
            this.restoreOriginalFavicon();
            return;
        }

        this.createFaviconWithBadge(badgeData.totalUnread);
    }

    /**
     * Opretter en favicon med badge overlay
     */
    private createFaviconWithBadge(count: number): void {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = 32;
        canvas.height = 32;

        // Hent original favicon
        const originalFavicon = this.getOriginalFavicon();
        if (originalFavicon) {
            const img = new Image();
            img.onload = () => {
                // Tegn original favicon
                ctx.drawImage(img, 0, 0, 32, 32);

                // Tegn badge cirkel
                ctx.fillStyle = "#f44336";
                ctx.beginPath();
                ctx.arc(24, 8, 8, 0, 2 * Math.PI);
                ctx.fill();

                // Tegn tekst
                ctx.fillStyle = "white";
                ctx.font = "bold 12px Arial";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";

                const text = count > 99 ? "99+" : count.toString();
                ctx.fillText(text, 24, 8);

                // Opdater favicon
                this.updateFavicon(canvas.toDataURL());
            };
            img.src = originalFavicon;
        }
    }

    /**
     * Henter original favicon URL
     */
    private getOriginalFavicon(): string | null {
        const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (faviconLink) {
            return faviconLink.href;
        }

        // Fallback til standard favicon
        const favicon = document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement;
        return favicon ? favicon.href : "/favicon.ico";
    }

    /**
     * Opdaterer favicon med ny URL
     */
    private updateFavicon(dataUrl: string): void {
        let faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;

        if (!faviconLink) {
            faviconLink = document.createElement("link");
            faviconLink.rel = "icon";
            document.head.appendChild(faviconLink);
        }

        faviconLink.href = dataUrl;
    }

    /**
     * Gendanner original favicon
     */
    private restoreOriginalFavicon(): void {
        const originalFavicon = this.getOriginalFavicon();
        if (originalFavicon) {
            this.updateFavicon(originalFavicon);
        }
    }

    /**
     * Manuelt opdaterer badge (kan kaldes fra andre komponenter)
     */
    public refreshBadge(): void {
        this.ulaesteDokumenterService.opdaterUlaesteDokumenter();
    }
}
